/**
 * CODEX - SOURCE UNIQUE DE VERITE
 *
 * Ce fichier est le point central obligatoire de documentation du lore, des
 * mecaniques et des contenus du jeu.  Chaque fois qu'un element est ajoute au
 * jeu (classe, arme, armure, monstre, competences, statut, element, consommable,
 * familier, titre, lore), il DOIT egalement etre reference ici.
 *
 * Pour garantir la coherence :
 * 1. Cree l'entite dans son fichier metier habituel (items.js, skillTrees.js...)
 * 2. Ajoute immediatement une entree dans le tableau CODEX_ENTRIES avec toutes
 *    les metadonnees pertinentes (voir le workflow .windsurf/workflows/update-codex.md).
 * 3. Si l'entree depend d'une decouverte en jeu, renseigne `unlockCondition`.
 *
 * Le store codex.js lit ce fichier pour afficher/masquer les entrees.
 */

import { reactive, watch } from 'vue';
import { REGISTRIES } from '../../stores/contentRegistry.js';
import { getElementLabel, getElementIcon, elementCounter } from '../combat/elements.js';
import { describeConsumableEffect, describeSkillMechanics } from '../combat/combatDescriptions.js';
import { BESTIAIRE } from '../lore/bestiaire.js';
import { LIFE_SPELLS, findSkillAnywhere } from '../progression/lifeTree.js';
import { MONSTER_SPRITES, getMonsterSprite } from './monsterSprites.js';

export const CODEX_CATEGORIES = [
  { id: 'lore',        label: 'Chroniques',    icon: '📜', defaultUnlocked: false },
  { id: 'classes',     label: 'Classes',         icon: '⚔️', defaultUnlocked: false },
  { id: 'skills',      label: 'Compétences',   icon: '✨', defaultUnlocked: false },
  { id: 'elements',    label: 'Éléments',      icon: '🔥', defaultUnlocked: false },
  { id: 'status',      label: 'Altérations',   icon: '☠️', defaultUnlocked: false },
  { id: 'equipment',   label: 'Équipement',    icon: '🛡️', defaultUnlocked: false },
  { id: 'consumables', label: 'Consommables',  icon: '🧪', defaultUnlocked: false },
  { id: 'monsters',    label: 'Bestiaire',     icon: '👹', defaultUnlocked: false },
  { id: 'dungeons',    label: 'Donjons',       icon: '🗼', defaultUnlocked: false },
  { id: 'familiars',   label: 'Familiers',     icon: '🐾', defaultUnlocked: false },
  { id: 'titles',      label: 'Titres',        icon: '🏅', defaultUnlocked: false }
];

/**
 * Forme canonique d'une entree de codex.
 *
 * id: identifiant unique
 * category: doit correspondre a l'id d'une categorie de CODEX_CATEGORIES
 * name: titre affiche
 * icon: emoji/iconographie
 * shortDesc: description visible meme si l'entree n'est pas encore decouverte
 * fullDesc: description complete visible apres decouverte
 * meta: objet libre pour lister les caracteristiques chiffrees
 * unlockCondition: 'default' (deverrouille automatiquement), 'encounter:<id>', 'kill:<id>', 'obtain:<id>', 'level:<n>', 'class:<id>', 'none' (jamais automatique)
 * alwaysVisibleForAdmin: true pour les admins (acces total)
 */
export const CODEX_ENTRIES = reactive(buildCodexEntries(REGISTRIES));

export function buildCodexEntries(reg: {
  classes: Record<string, any>;
  skillTrees: Record<string, any>;
  items: Record<string, any>;
  statusEffects: Record<string, any>;
  elements: Record<string, any>;
  titles: Record<string, any>;
}) {
  const CLASSES = reg.classes;
  const SKILL_TREES = reg.skillTrees;
  const ITEMS = reg.items;
  const STATUS_EFFECTS = reg.statusEffects;
  const ELEMENTS = reg.elements;

  // Ids des sorts d'école de classe → évite un doublon si un Rameau de Vie
  // partage un id avec un sort de classe.
  const classSkillIds = new Set(Object.values(SKILL_TREES).flat().map((s: any) => s.id));

  return [
    // ================= LORE =================
    {
      id: 'lore_guild_origins',
      category: 'lore',
      name: 'Les Origines de la Guilde',
      icon: '🏰',
      shortDesc: 'Les premieres pierres de la Guilde furent posees bien avant l’Age des Tenebres.',
      fullDesc: 'Fondee par les anciens Conseillers pour contenir les creatures surgies des failles, la Guilde rassemble aventuriers, marchands et erudits. Chaque rang acquis est une reconnaissance du service rendu au monde.',
      meta: { discovered: 'Taverne' },
      unlockCondition: 'default'
    },
    {
      id: 'lore_civic_quests',
      category: 'lore',
      name: 'Les Devoirs civiques',
      icon: '📜',
      shortDesc: 'Les devoirs civiques demandent un geste réel au lieu indiqué.',
      fullDesc: 'Le lieu marque le début du devoir : une fois sur place, l’aventurier agit, observe, découvre ou vérifie quelque chose de réel. Les déchets, espèces et gestes utiles sont inscrits au registre par des tampons. Le minimum ouvre le droit de sceller la quête, mais le registre reste ouvert à tous les gestes supplémentaires sans multiplier artificiellement la prime.',
      meta: { accès: 'proximité GPS', validation: 'action · observation · découverte', registre: 'tampons illimités' },
      unlockCondition: 'default'
    },
    {
      id: 'lore_mandatary_journeys',
      category: 'lore',
      name: 'Les Parcours des mandataires',
      icon: '🗺️',
      shortDesc: 'Un avis, plusieurs points à visiter et un carnet de progression personnel.',
      fullDesc: 'Un mandataire peut relier plusieurs lieux à son avis de quête. Chaque étape annonce son titre, son objectif et les éventuels objets à collecter. Le parcours précise son ordre et son mode de validation. Le joueur accepte puis scanne lui-même le QR de chaque point, sans dépendre du GPS. Une visite peut être l’objectif demandé. Après les scans requis, la quête se termine automatiquement ou attend la validation finale du mandataire sur récapitulatif. Le carnet conserve les scans après reconnexion ; leur enregistrement ne prouve pas à lui seul une remise matérielle. Ces QR sont réservés aux parcours, et une photographie du code peut être partagée.',
      meta: { ordre: 'libre ou imposé', validation: 'automatique ou mandataire', registre: 'scans et horaires par aventurier' },
      unlockCondition: 'default'
    },
    {
      id: 'lore_offered_rewards',
      category: 'lore',
      name: 'Les Lots offerts',
      icon: '🎁',
      shortDesc: 'Un souvenir ou un objet offert, distinct des gains de la Guilde.',
      fullDesc: 'Un mandataire peut offrir un lot non monétaire dans une quête ordinaire, un parcours ou un événement. La fiche annonce sa nature, sa quantité par aventurier et les modalités de retrait. Argent et équivalents monétaires sont exclus. Le lot ne multiplie ni la prime en Cuivres ni la réputation. Accomplir la quête et retirer le lot sont deux étapes distinctes : le mandataire enregistre la remise une seule fois. Un lot d’événement suit les conditions annoncées, sans être automatiquement réservé au meilleur chasseur.',
      meta: { nature: 'non monétaire', retrait: 'conditions annoncées', remise: 'distincte de l’accomplissement' },
      unlockCondition: 'default'
    },
    {
      id: 'lore_event_hunts',
      category: 'lore',
      name: 'Les Chasses d’événement',
      icon: '🐾',
      shortDesc: 'Des monstres reviennent pendant toute la durée de l’animation.',
      fullDesc: 'Les nouvelles chasses d’événement se créent gratuitement : l’organisateur choisit les espèces et la durée. Les aventuriers ayant rejoint l’événement peuvent chasser ses monstres. Les apparitions sont aléatoires ; leur niveau est la moyenne arrondie des niveaux des participants présents dans l’application, plus deux. Chaque monstre garde ensuite son niveau. Sans participant présent, les apparitions attendent. Les meutes se forment selon les règles habituelles. Une inscription ancienne ne suffit pas à maintenir un joueur dans la moyenne. Les événements autrefois payés en Essence conservent leur contrat historique.',
      meta: { création: 'gratuite', niveau: 'moyenne des présents + 2', meutes: 'formation automatique' },
      unlockCondition: 'default'
    },
    {
      id: 'lore_event_queue',
      category: 'lore',
      name: 'Le Registre des passages',
      icon: '🔔',
      shortDesc: 'Prendre un numéro, chasser en attendant et retrouver son appel.',
      fullDesc: 'Un événement peut proposer une file d’attente. Rejoindre l’événement et prendre un numéro sont deux choix distincts, sans QR de parcours. Le mandataire appelle le suivant ou un numéro précis, puis suit sa prise en charge. Fermer les nouvelles entrées conserve les tickets déjà pris. La fin de la chasse ne supprime pas la file ; terminer son passage ne retire pas l’accès à l’événement. Le joueur retrouve son ticket après reconnexion. Pour recevoir l’appel en direct, il garde l’application connectée.',
      meta: { accès: 'option de l’événement', appel: 'suivant ou numéro', fermeture: 'nouvelles entrées ou événement' },
      unlockCondition: 'default'
    },
    {
      id: 'lore_atb_system',
      category: 'lore',
      name: 'La Barre ATB',
      icon: '⏳',
      shortDesc: 'Le temps d’attente entre chaque action depend de la Vitalite.',
      fullDesc: 'Chaque combattant remplit sa barre ATB a une vitesse basee sur sa VIT. Une fois pleine, il peut agir. Les competences et consommables consomment le tour en cours.',
      meta: { stat: 'VIT', maxBar: 1000 },
      unlockCondition: 'default'
    },
    // ── Donjons v2 (MAJ-08) — règles du monde souterrain ──
    {
      id: 'dungeon_hamlet_housing',
      category: 'dungeons',
      name: 'Les Maisons du hameau',
      icon: '🏘️',
      shortDesc: 'Un accueil commun et un lit pour chaque habitant.',
      fullDesc: 'La gardienne du sanctuaire accueille les voyageurs à son comptoir. Maël tient une boutique avec sa chambre privée ; le forgeron dispose lui aussi de son atelier abrité et de son propre lit. Les autres habitants vivent dans des maisons à chambres séparées. Les bâtiments se répartissent autour de cours et de rues, avec des pignons, lucarnes, colombages, plantes et livres qui racontent leurs usages. Chaque habitant possède un lit accessible par une porte, sans traverser les cloisons.',
      meta: { accueil: 'gardienne du sanctuaire', logement: 'un lit par habitant', portes: 'F ou bouton Ouvrir à proximité ; fermeture automatique à distance, y compris dans les chambres.' },
      unlockCondition: 'default'
    },
    {
      id: 'dungeon_world_chronicle',
      category: 'dungeons',
      name: 'La Chronique mondiale',
      icon: '🗺️',
      shortDesc: 'Une entrée ancrée, une expédition accessible partout.',
      fullDesc: 'Les donjons sont rares : la Guilde les consigne donc tous dans une Chronique mondiale accessible depuis le Nexus. Leur ancrage nourrit la carte, leur histoire et la recherche par région ou distance, mais ne bloque jamais l’entrée. Niveau, certification, escouade et sceaux restent les seules gardes d’expédition.',
      meta: { accès: 'mondial', recherche: 'nom · clan/sponsor · région · rayon' },
      unlockCondition: 'default'
    },
    {
      id: 'dungeon_strata',
      category: 'dungeons',
      name: 'Les Strates',
      icon: '🌒',
      shortDesc: 'Plus on descend, plus le donjon change de visage.',
      fullDesc: 'Chaque donjon est découpé en strates : des tranches d’étages qui partagent un même univers (cryptes, forêts englouties, abîmes ardents…). Franchir une frontière de strate, c’est changer de monde — les étages y sont aussi de plus en plus vastes.',
      meta: { taille: 'surface croissante par étage, indépendante du biome' },
      unlockCondition: 'default'
    },
    {
      id: 'dungeon_secret_key',
      category: 'dungeons',
      name: 'Clé de salle secrète',
      icon: '🗝️',
      shortDesc: 'Une clé pour une salle, dans un donjon et à un étage précis.',
      fullDesc: 'Un monstre de l’étage porte la clé de sa salle secrète. Le Paquetage et la besace affichent le donjon et l’étage liés. Le menu rapide présente les clés utilisables à cet étage, puis les potions, puis les autres clés grisées. La clé indique la direction de la porte pendant dix secondes ou l’ouvre si tu es à proximité ; elle est consommée à l’ouverture. Tu peux la jeter définitivement après confirmation, sans contrepartie. Les anciennes clés dont le donjon n’a pas été enregistré affichent une origine inconnue.',
      meta: { usage: 'salle secrète', suppression: 'définitive, après confirmation' },
      unlockCondition: 'default'
    },
    {
      id: 'dungeon_conditional_surprises',
      category: 'dungeons',
      name: 'Les Secrets du donjon',
      icon: '🧩',
      shortDesc: 'Un coffre ou une victoire peut changer ce que révèle un étage.',
      fullDesc: 'Le créateur peut lier une embuscade, un coffre ou une chambre à un événement précis : ouverture d’un coffre désigné, victoire sur une présence ou nettoyage des ennemis requis. Une chambre mécanique existe déjà derrière sa paroi ; le mur coulisse quand sa condition est remplie. Elle ne perce jamais une autre entrée dans une salle à clé : celle-ci reste entièrement scellée jusqu’à l’utilisation de sa propre clé. Une récompense ne doit pas être la condition de sa propre apparition.',
      meta: { événements: 'coffre · victoire · nettoyage', accès: 'clé ou mécanisme distinct' },
      unlockCondition: 'default'
    },
    {
      id: 'dungeon_reserve',
      category: 'dungeons',
      name: 'La Réserve d’Étage',
      icon: '🪙',
      shortDesc: 'Le trésor d’un étage n’est pas infini.',
      fullDesc: 'Chaque étage possède une réserve de cuivres, partagée entre tous les aventuriers. Monstres vaincus et coffres ouverts y puisent — un étage pillé ne rapporte plus rien. Aucun cuivre ne sort de nulle part : c’est le circuit fermé de la Guilde.',
      meta: { source: 'sponsor ou Guilde', partage: 'tous les aventuriers' },
      unlockCondition: 'default'
    },
    {
      id: 'dungeon_record',
      category: 'dungeons',
      name: 'Le Record d’Ascension',
      icon: '⏫',
      shortDesc: 'L’étage le plus haut atteint reste acquis.',
      fullDesc: 'La Guilde consigne votre meilleure ascension dans chaque donjon : vous pouvez reprendre directement à votre record. En escouade, le groupe entre à la hauteur du MOINS avancé de ses membres — on ne porte personne au sommet.',
      meta: { escouade: 'minimum des records' },
      unlockCondition: 'default'
    },
    {
      id: 'dungeon_ko',
      category: 'dungeons',
      name: 'Chute et Relevailles',
      icon: '💀',
      shortDesc: 'Tomber n’est pas mourir — mais ça se paie.',
      fullDesc: 'Un KO vous ramène au point de départ de l’étage, relevé à la moitié de vos PV ; les monstres déjà vaincus restent vaincus. Trois échecs contre le maître d’étage mettent fin à la run — les gains restent acquis.',
      meta: { ko: '50 % PV', essaisMaitre: 3 },
      unlockCondition: 'default'
    },
    {
      id: 'dungeon_vitals',
      category: 'dungeons',
      name: 'L’Endurance des Profondeurs',
      icon: '🧪',
      shortDesc: 'Les blessures suivent d’un combat à l’autre.',
      fullDesc: 'En donjon, PV, PM et endurance ne se régénèrent pas entre les combats : l’état dans lequel vous sortez d’un affrontement est celui dans lequel vous entamez le suivant, fuite comprise. Prévoyez des potions.',
      meta: { persiste: 'PV · PM · endurance' },
      unlockCondition: 'default'
    },
    {
      id: 'dungeon_master',
      category: 'dungeons',
      name: 'Le Maître d’Étage',
      icon: '👑',
      shortDesc: 'Quelqu’un garde toujours la sortie.',
      fullDesc: 'Au sommet de chaque étage important veille un maître : monstre élite, boss du monde… et un jour, le champion d’un clan. Tant qu’il tient, la sortie reste scellée. Son anneau doré se repère de loin.',
      meta: { types: 'élite · world boss · build de joueur (à venir)' },
      unlockCondition: 'default'
    },
    {
      id: 'lore_primary_stats',
      category: 'lore',
      name: 'Les Stats Primaires',
      icon: '❤️',
      shortDesc: 'PV, ARMURE, RM et VITESSE forment le socle de survie du personnage.',
      fullDesc: 'Les stats primaires sont les caracteristiques fondamentales du combattant : PV (Points de Vie) augmentent l\'endurance, ARMURE reduit les degats physiques, RM (Resistance Magique) reduit les degats magiques de tous les elements, et VITESSE accelere le remplissage de la barre ATB. Chaque niveau octroie 1 point primaire a depenser librement.',
      meta: {
        pointsPerLevel: 1,
        PV: '1 point = +5 PV',
        ARMURE: '1 point = +1 armure',
        RM: '1 point = +1 resistance magique',
        VITESSE: '1 point = +0.2 vitesse ATB'
      },
      unlockCondition: 'default'
    },
    {
      id: 'lore_secondary_stats',
      category: 'lore',
      name: 'Les Stats Secondaires',
      icon: '📊',
      shortDesc: 'FOR, INT, SAG, DEX et CHA dictent les affinites et le style de combat.',
      fullDesc: 'Les stats secondaires sont les attributs de personnage : FOR booste les coups physiques, INT les sorts offensifs, SAG les soins et la protection, DEX la precision et l\'esquive, et CHA renforce les effets de charme, de soutien et de chants du Barde. Les points secondaires s\'obtiennent a chaque niveau et peuvent etre reinitialises dans le profil.',
      meta: {
        pointsPerLevel: 3,
        FOR: 'Degats physiques et armure',
        INT: 'Degats magiques offensifs',
        SAG: 'Soins et sorts defensifs',
        DEX: 'Esquive et precision',
        CHA: 'Charme, soutien et chants bardiques'
      },
      unlockCondition: 'default'
    },
    {
      id: 'lore_elements',
      category: 'elements',
      name: 'Affinite et Resistance Elementaires',
      icon: '🌈',
      shortDesc: 'Les stats tertiaires ameliorent le pouvoir et la protection contre chaque element.',
      fullDesc: 'Chaque element (feu, glace, foudre, saint, sombre, nature, terre, vent, eau) possede une valeur d\'affinite et de resistance. L\'affinite augmente les degats infliges avec cet element, tandis que la resistance reduit les degats subis. Les stats globales se repartissent automatiquement sur tous les elements avec un facteur. Les points tertiaires s\'obtiennent tous les 3 niveaux.',
      meta: {
        pointsPerLevel: '1 tous les 3 niveaux',
        elements: 'fire, ice, lightning, holy, dark, nature, earth, wind, water',
        globalRatio: '0.2'
      },
      unlockCondition: 'default'
    },
    {
      id: 'lore_barrier',
      category: 'lore',
      name: 'Barriere et Altérations',
      icon: '🛡️',
      shortDesc: 'La barriere absorbe les degats directs, mais pas les effets sur la duree.',
      fullDesc: 'La barriere est une protection temporaire qui absorbe les degats des attaques directes avant qu\'ils ne touchent les PV. En revanche, les alterations sur la duree comme le saignement, le poison ou la brulure contournent la barriere et blessent directement la sante du combattant.',
      meta: {
        absorption: '100% des degats directs absorbes par la barriere en priorite',
        ignoredBy: 'saignement, poison, brulure et tout effet dot',
        display: 'barre bleue transparente superposee aux PV'
      },
      unlockCondition: 'default'
    },

    // ================= CLASSES =================
    ...Object.values(CLASSES).map((c) => ({
      id: `class_${c.id}`,
      category: 'classes',
      name: c.label,
      icon: c.icon,
      shortDesc: c.desc,
      fullDesc: `${c.label} suit la branche ${c.branch || 'offensive'}. ${c.lore || 'Un aventurier dont les talents se revelent au fil des combats.'}`,
      meta: {
        baseStats: c.baseStats,
        growth: c.growth,
        startingSkills: (SKILL_TREES[c.id] || []).slice(0, 3).map((s: any) => s.name).join(', ')
      },
      unlockCondition: c.id === 'guerrier' ? 'default' : `class:${c.id}`
    })),

    // ================= COMPETENCES =================
    ...Object.entries(SKILL_TREES).flatMap(([classId, tree]) =>
      (tree as any[]).map((s: any) => ({
        id: `skill_${s.id}`,
        category: 'skills',
        name: s.name,
        icon: s.icon,
        shortDesc: s.desc,
        fullDesc: `${s.desc} ${describeSkillMechanics(s).join(' ')} Compétence universelle de l'Arbre Mycélien, héritée de la voie ${CLASSES[classId]?.label || classId} : toute voie de départ peut la débloquer en remplissant ses prérequis.`,
        meta: {
          class: CLASSES[classId]?.label || classId,
          type: s.type,
          element: s.element,
          cost: s.cost,
          cooldown: s.cooldown,
          minLevel: s.minLevel,
          skillPoints: s.skillPoints,
          target: s.target,
          affinity: s.affinity,
          effect: s.effect,
          statuses: s.statuses,
        },
        unlockCondition: `level:${s.minLevel || 1}`
      }))
    ),

    // ---- Arbre Mycélien / Rameaux de Vie (LIFE_SPELLS) : le VRAI système de
    // sorts, débloqué par stats/affinités et non par classe. Absent du codex
    // jusqu'ici alors qu'il compte l'essentiel des sorts du jeu. ----
    ...LIFE_SPELLS.filter((s: any) => !classSkillIds.has(s.id)).map((s: any) => ({
      id: `skill_${s.id}`,
      category: 'skills',
      name: s.name,
      icon: s.icon || '✨',
      shortDesc: s.desc,
      fullDesc: `${s.desc} ${describeSkillMechanics(s).join(' ')} Rameau de Vie universel${s.branch ? ` — branche ${s.branch}` : ''}${s.tier != null ? `, palier ${s.tier}` : ''}. Débloqué par ses prérequis, indépendamment de la voie de départ.`,
      meta: {
        source: 'Arbre Mycélien',
        type: s.type,
        element: s.element,
        cost: s.cost,
        cooldown: s.cooldown,
        minLevel: s.minLevel,
        skillPoints: s.skillPoints,
        branch: s.branch,
        tier: s.tier,
        unlock: s.unlock,
        target: s.target,
        affinity: s.affinity,
        effect: s.effect,
        statuses: s.statuses,
      },
      unlockCondition: `level:${s.minLevel || 1}`,
    })),

    // ================= ELEMENTS =================
    ...Object.keys(ELEMENTS).filter(el => el !== 'physical').map((el) => ({
      id: `element_${el}`,
      category: 'elements',
      name: getElementLabel(el),
      icon: getElementIcon(el),
      shortDesc: `Un element fondamental du systeme de combat.`,
      fullDesc: `L'element ${getElementLabel(el)} possede des forces et des faiblesses face aux autres elements. Utilisez-le pour maximiser vos degats ou reduire ceux subis.`,
      meta: { element: el },
      unlockCondition: 'default'
    })),

    // ================= ALTERATIONS =================
    ...Object.values(STATUS_EFFECTS).map((s) => ({
      id: `status_${s.id}`,
      category: 'status',
      name: s.name,
      icon: s.icon,
      shortDesc: s.desc,
      fullDesc: `${s.desc}${s.ignoresBarrier ? ' Ce statut ignore la barrière de protection et inflige ses dégâts directement aux PV.' : ''}`,
      meta: {
        duration: s.duration,
        stackable: s.stackable,
        maxStacks: s.maxStacks,
        effect: s.statMod || s.dot || s.hot || s.shield || s.revive || s.charm,
        ignoresBarrier: s.ignoresBarrier || false
      },
      unlockCondition: 'default'
    })),

    // ================= EQUIPEMENT =================
    {
      id: 'equipment_procedural_dungeons',
      category: 'equipment',
      name: 'Butin des profondeurs',
      icon: '⚒️',
      shortDesc: 'Les équipements de combat se gagnent dans les donjons.',
      fullDesc: 'Chaque pièce trouvée est une instance unique liée à son aventurier. Son niveau d’objet fixe son budget, sa rareté détermine le nombre d’affixes et ses étoiles indiquent la qualité du jet. Les affixes renforcent les attributs, les affinités ou les résistances. La boutique ne vend aucun équipement donnant des statistiques.',
      meta: { raretes: 'commun, rare, épique, légendaire', qualite: '1 à 5 étoiles', commerce: 'lié au ramassage' },
      unlockCondition: 'default'
    },
    {
      id: 'equipment_skill_thresholds',
      category: 'skills',
      name: 'Seuils de maîtrise',
      icon: '🔒',
      shortDesc: 'Une compétence apprise peut devenir temporairement inutilisable.',
      fullDesc: 'Les prérequis sont vérifiés avec les statistiques du build courant, équipement compris. Si un attribut, une affinité, une résistance ou une compétence prérequise passe sous son seuil, la technique reste apprise mais ne peut plus être lancée. Restaurer le seuil la réactive sans nouveau coût.',
      meta: { apprentissage: 'permanent', disponibilite: 'dynamique selon le build' },
      unlockCondition: 'default'
    },
    {
      id: 'guild_certifications',
      category: 'lore',
      name: 'Certifications de la Guilde',
      icon: '📜',
      shortDesc: 'Chaque examen officiel autorise vingt niveaux supplémentaires.',
      fullDesc: 'La réputation obtenue par l’entraide détermine le rang, mais le passage au palier suivant exige aussi un examen de la Guilde. Les certifications G, F, E, D, C, B, A, S, SS et SSS ouvrent successivement les niveaux jusqu’au plafond 200. Au plafond courant, une seule jauge de niveau peut être mise en réserve.',
      meta: { niveauxParCertification: 20, niveauMaximum: 200, dernierRang: 'SSS' },
      unlockCondition: 'default'
    },

    ...Object.values(ITEMS).filter(i => i.slot && i.slot !== 'avatarDataUrl' && i.cat !== 'consommable' && i.cat !== 'familiar' && i.cat !== 'lyra' && i.cat !== 'frame' && i.cat !== 'taverne_bg' && i.cat !== 'particles' && i.cat !== 'theme').map((i) => ({
      id: `item_${i.id}`,
      category: 'equipment',
      name: i.name,
      icon: i.emoji || '⚔️',
      img: i.img,
      shortDesc: i.desc,
      fullDesc: `${i.desc} Slot: ${i.slot}. Ce modèle peut être découvert sous forme d’instance unique dans un donjon.`,
      meta: {
        slot: i.slot,
        rarity: i.rarity,
        price: i.price,
        stats: i.stats
      },
      unlockCondition: `obtain:${i.id}`
    })),

    // ================= CONSOMMABLES =================
    ...Object.values(ITEMS).filter(i => i.cat === 'consommable').map((i) => ({
      id: `consumable_${i.id}`,
      category: 'consumables',
      name: i.name,
      icon: i.emoji || '🧪',
      shortDesc: i.desc,
      fullDesc: describeConsumableEffect(i, { resolveSkill: id => findSkillAnywhere(id, SKILL_TREES) }),
      meta: {
        combat: i.combat
      },
      unlockCondition: i.id === 'potion_soin' ? 'default' : `obtain:${i.id}`
    })),

    // ================= FAMILIERS =================
    ...Object.values(ITEMS).filter(i => i.cat === 'familiar').map((i) => ({
      id: `familiar_${i.id}`,
      category: 'familiars',
      name: i.name,
      icon: i.emoji || '🐾',
      shortDesc: i.desc,
      fullDesc: `${i.desc} Chaque familiar possede des competences uniques qu'il peut utiliser en combat.`,
      meta: {
        price: i.price,
        rarity: i.rarity
      },
      unlockCondition: `obtain:${i.id}`
    })),

    // ================= BESTIAIRE =================
    {
      id: 'monster_packs',
      category: 'monsters',
      name: 'Meutes de monstres',
      icon: '👥',
      shortDesc: 'Certains monstres forment des meutes quand ils se croisent.',
      fullDesc: 'Les créatures hostiles ne sont pas toutes solitaires. Quand deux monstres compatibles restent assez longtemps à proximité, ils fusionnent en une meute jusqu\'à un maximum de quatre individus. La meute gagne en puissance avec chaque membre supplémentaire : +10% de dégâts et d\'armure par monstre au-delà du leader, jusqu\'à +30%. Si tous les membres partagent le même élément, la cohésion élémentaire accorde un bonus supplémentaire de 15%. Une meute se dissout si ses membres sont vaincus ou s\'éloignent trop les uns des autres.',
      meta: {
        maxSize: 4,
        proximity: '80 mètres',
        formationTime: '~10 secondes',
        cohesionBonus: '+10% par membre supplémentaire (max +30%)',
        affinityBonus: '+15% si même élément',
        compatibilities: 'La relation packsWith doit être réciproque dans le bestiaire',
        example: 'Gobelins, squelettes et zombies forment facilement des meutes'
      },
      unlockCondition: 'default'
    },
    ...BESTIAIRE.map((m) => ({
      id: `monster_${m.id.replace('m_', '')}`,
      category: 'monsters',
      name: m.nom,
      icon: m.icon,
      img: m.img,
      sprite: getMonsterSprite(m.id),
      shortDesc: m.desc,
      fullDesc: m.desc,
      meta: {
        level: m.baseLevel,
        damage: m.baseDmg,
        armor: m.armure,
        hp: Math.round(40 + m.baseLevel * 10 * m.hpMult),
        danger: m.baseLevel >= 30 ? 'extrême' : m.baseLevel >= 15 ? 'élevé' : m.baseLevel >= 5 ? 'modéré' : 'faible',
        // Identité élémentaire (maj-05) : son type + sa VRAIE faiblesse (contre
        // canonique, aligné sur sa résistance). Avant : `statsPotential` lisait
        // un champ `fundamentalStats` inexistant → ranges bidons identiques.
        element: m.element,
        weakness: elementCounter(m.element),
        skills: (m.skills || []).map((s: any) => ({
          name: s.name, icon: s.icon, element: s.element, type: s.type, desc: s.desc,
        })),
      },
      unlockCondition: `encounter:monster_${m.id.replace('m_', '')}`
    })),

    // ================= TITRES =================
    ...Object.values(reg.titles).map((t) => ({
      id: `title_${t.id}`,
      category: 'titles',
      name: t.name,
      icon: t.icon || '🏅',
      img: t.img,
      shortDesc: t.desc,
      fullDesc: t.desc,
      meta: {
        secret: !!t.secret,
        condition: t.conditions,
        rewards: t.rewards
      },
      unlockCondition: t.id === 'recruit' ? 'default' : 'none'
    }))
  ];
}

watch(REGISTRIES, () => {
  const next = buildCodexEntries(REGISTRIES);
  CODEX_ENTRIES.splice(0, CODEX_ENTRIES.length, ...next);
}, { deep: true });

/**
 * Helper : retourne les entrees d'une categorie, pretes a etre affichees.
 */
export function getCodexEntries(category = null) {
  if (!category) return CODEX_ENTRIES;
  return CODEX_ENTRIES.filter(e => e.category === category);
}

/**
 * Helper : retourne une entree par id.
 */
export function getCodexEntry(id: string) {
  return CODEX_ENTRIES.find(e => e.id === id) || null;
}
